import React, {Fragment, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect, useSelector, useDispatch} from 'react-redux';
import SparePartDeleteForm from "./SparePartDeleteForm";
import SparePartUpdateForm from "./SparePartUpdateForm";
import {getSpareParts} from "../../actions/spare_part";
import SparePartCreateForm from "./SparePartCreateForm";
import SparePartItem from "./SparePartItem";
import SearchForm from "../layout/SearchForm";
import FilterByInStock from "./FilterByInStock";
import { Plus, Filter} from "react-feather";
import FilterByFirm from "../filters/FilterByFirm";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import TableSpinner from "../layout/TableSpinner";
import { usePermission } from '../../hooks/usePermission';
import { Button, Modal } from 'react-bootstrap';
import FilterWithTrashed from "../filters/FilterWithTrashed";
import { getFirmOptionsForManagers } from '../../actions/user';

// Create a DeletedSparePartsFilterModal component
const DeletedSparePartsFilterModal = ({ show, handleClose, getPageItems, dispatch, onlyTrashed }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan ehtiyot qism filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan ehtiyot qismlar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha ehtiyot qismlarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <input 
                                className="form-check-input"
                                type="checkbox"
                                id="filterOnlyTrashedSpareParts"
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    
                                    const url = new URL(window.location);
                                    url.searchParams.set('page', 1);
                                    
                                    if (checked) {
                                        url.searchParams.set('only_trashed', 'true');
                                        url.searchParams.delete('with_trashed');
                                    } else {
                                        url.searchParams.delete('only_trashed');
                                    }
                                    
                                    window.history.pushState({}, '', url);
                                    
                                    // Get updated params for API call
                                    const newParams = new URLSearchParams(window.location.search);
                                    const apiParams = {};
                                    for (const [key, value] of newParams.entries()) {
                                        apiParams[key] = value;
                                    }
                                    
                                    dispatch(getSpareParts(1, apiParams));
                                }}
                                checked={onlyTrashed}
                            />
                            <label className="form-check-label" htmlFor="filterOnlyTrashedSpareParts">
                                Faqat o'chirilgan ehtiyot qismlar
                            </label>
                        </div>
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan ehtiyot qismlarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const SpareParts = () => {
    const { userCan } = usePermission();
    const dispatch = useDispatch();
    const {spare_parts, loading, numberOfPages, currentPage} = useSelector(state => state.spare_part);
    const auth = useSelector(state => state.auth);
    const [show, setShow] = useState(false);
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    // Load spare parts data when component mounts
    useEffect(() => {
        dispatch(getSpareParts());
    }, [dispatch]);

    // Load firm data for permission checks
    useEffect(() => {
        if (auth.user?.role_name === 'manager' || auth.user?.role_name === 'accountant') {
            dispatch(getFirmOptionsForManagers());
        }
    }, [dispatch, auth.user?.role_name]);

    if (!userCan('view spare parts')) {
        return <div>Sizda ehtiyot qismlarni ko'rish huquqi yo'q.</div>;
    }

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);

    return (
        <Fragment>
            <div className="container-fluid p-0">

                <div className="row mt-4">
                    <div className="col-md-6">
                        <h1 className="h5 mb-3"><strong>Ehtiyot qismlar</strong>({spare_parts.length}) </h1>
                    </div>
                    <div className="col-md-6 text-end">
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2"
                            onClick={handleDeletedFiltersShow}
                        >
                            <Filter size={16} className="me-1" />
                            O'chirilganlar
                            {hasDeletedFilters && <span className="badge bg-warning ms-1">1</span>}
                        </Button>
                        
                        {userCan('create spare parts') && (
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleShow}
                            >
                                <Plus size={16} className="me-1" />
                                Ehtiyot qism
                            </Button>
                        )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-3">
                        <FilterByFirm getPageItems={getSpareParts}/>
                    </div>

                    <div className="col-3">
                        <SearchForm getPageItems={getSpareParts}/>
                    </div>

                    <div className="col-3">
                        <FilterByInStock getPageItems={getSpareParts}/>
                    </div>

                    <div className="col-md-3">
                        {/* Create button moved to header */}
                    </div>
                </div>

                <div className="row mt-2">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <div className="card w-100 table-responsive">
                            {loading && <TableSpinner/>}
                            <table className="table custom-table table-hover">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Nomi</th>
                                    <th scope="col">Firma</th>
                                    <th scope="col">Sklad</th>
                                    <th scope="col">Qolgan miqdori</th>
                                    <th scope="col">O'lchov birligi</th>
                                    <th scope="col">So'nggi Narxi</th>
                                    <th scope="col">Amallar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {spare_parts && spare_parts.length > 0 ? (
                                    spare_parts.map((item, index) => (
                                        <SparePartItem
                                            key={item.id}
                                            spare_part={item}
                                            number={index + 1}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4">
                                            <div className="text-muted">
                                                {loading ? 'Ma\'lumotlar yuklanmoqda...' : 'Ehtiyot qismlar topilmadi'}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <CustomPaginationWithManyPages
                            numberOfPages={numberOfPages}
                            getPageItems={getSpareParts}
                            current_page={currentPage}
                        />
                    </div>
                </div>
            </div>

                                    {userCan('create spare parts') && (
                <SparePartCreateForm
                    show={show}
                    handleClose={handleClose}
                />
            )}

                                    {userCan('edit spare parts') && (
                <SparePartUpdateForm/>
            )}

                                    {userCan('delete spare parts') && (
                <SparePartDeleteForm/>
            )}

            <DeletedSparePartsFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getSpareParts}
                dispatch={dispatch}
                onlyTrashed={onlyTrashed}
            />
        </Fragment>
    );
};

SpareParts.propTypes = {
    getSpareParts: PropTypes.func.isRequired
};

export default connect(null, {getSpareParts})(SpareParts);
